import { checkoutPlatform } from '../lib/hostedCheckout'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Clock, CreditCard, Shield, Users } from 'lucide-react'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import BackPillButton from '../components/BackPillButton'
import MemberPaymentMethodOptions from '../components/MemberPaymentMethodOptions'
import { createPayNowHandoff, openPayNowCheckoutPlaceholder, subscribePayNowCompleted, updatePayNowHandoff } from '../utils/payNowTabHandoff'
import { commitMemberBookingSuccess, createCommittedBooking } from '../lib/upcomingBookingsCache'
import {
  DEFAULT_GOLF_TIME_ZONE,
  clampDateKey,
  formatLongDate,
  formatTimeInTimeZone,
  getDateKeyForInstant,
  getTodayDateKey,
  getZonedMinutes,
  getBookingWindowLimits,
  normalizeDateKey,
  parseDateKey,
  resolveCourseTimeZone,
} from '../utils/golfTime'

function formatDateLabel(value, timeZone, locale, memberText) {
  if (!value) return memberText("portalPresentation.member_select_date")
  const formatted = formatLongDate(value, timeZone, locale)
  return formatted || memberText("portalPresentation.member_select_date")
}

function formatTimeLabel(value, timeZone, locale) {
  return formatTimeInTimeZone(value, timeZone, locale)
}

const buildPlayer = (defaults = {}) => ({
  name: '',
  type: null,
  member_id: null,
  guestEmail: '',
  guestPhone: '',
  locked: false,
  ...defaults,
})

function normalizeDateFromSlot(slot, timeZone) {
  if (!slot?.slot_time) return null
  return getDateKeyForInstant(new Date(slot.slot_time), timeZone)
}

function getTimeBand(timeStr, timeZone) {
  if (!timeStr) return null
  const minutes = getZonedMinutes(timeStr, timeZone)
  if (!Number.isFinite(minutes)) return null

  const isPeakMorning = minutes >= 7 * 60 && minutes < 12 * 60
  const isPeakAfternoon = minutes >= 15 * 60 && minutes < 18 * 60

  return isPeakMorning || isPeakAfternoon ? 'peak' : 'offpeak'
}

function getDayType(dateStr) {
  if (!dateStr) return null
  const parsed = parseDateKey(dateStr)
  if (!(parsed instanceof Date) || Number.isNaN(parsed.getTime())) return null
  const day = parsed.getUTCDay()
  return day === 0 || day === 6 ? 'weekend' : 'weekday'
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export default function MemberTeeBookingPage({ clubCode, member }) {
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const { courseId, teeTimeId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { memberPortalApiGet, memberPortalApiPost } = useMemberPortalApi()

  useEffect(() => subscribePayNowCompleted({
    module: 'golf',
    clubCode,
    onCompleted: (data) => navigate(`/${clubCode}/golf/booking/success?booking_id=${data.booking_id}`, { replace: true }),
  }), [clubCode, navigate])

  const initialSlotState = location.state?.slot || null
  const [course, setCourse] = useState(location.state?.course || null)
  const [slot, setSlot] = useState(initialSlotState)
  const [timeZone, setTimeZone] = useState(DEFAULT_GOLF_TIME_ZONE)
  const [selectedDateKey, setSelectedDateKey] = useState(() => normalizeDateKey(searchParams.get('date')))
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [addons, setAddons] = useState([])
  const [selectedAddonsByKey, setSelectedAddonsByKey] = useState({})
  const [holes, setHoles] = useState(null)
  const [saving, setSaving] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('member_account')
  const [formMessage, setFormMessage] = useState(null)
  const [submitError, setSubmitError] = useState(null)
  useEffect(() => {
    const closed = () => setSubmitError('memberAccount.checkoutClosed')
    window.addEventListener('members:checkout-closed', closed)
    window.addEventListener('members:checkout-cancelled', closed)
    return () => { window.removeEventListener('members:checkout-closed', closed); window.removeEventListener('members:checkout-cancelled', closed) }
  }, [])
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  const [currencyCode, setCurrencyCode] = useState('GBP')
  const [currencySymbol, setCurrencySymbol] = useState('£')
  const [pricing, setPricing] = useState([])
  const [liveQuoteTotal, setLiveQuoteTotal] = useState(null)
  const [liveQuoteLoading, setLiveQuoteLoading] = useState(false)
  const [quoteRetry, setQuoteRetry] = useState(0)
  const [liveQuoteError, setLiveQuoteError] = useState(null)
  const [memberResultsByIndex, setMemberResultsByIndex] = useState({})
  const memberSearchControllersRef = useRef({})
  const memberSearchTimersRef = useRef({})
  const [openGroupEnabled, setOpenGroupEnabled] = useState(false)
  const [openGroupSpots, setOpenGroupSpots] = useState(null)
  const [openGroupMinHcp, setOpenGroupMinHcp] = useState('')
  const [openGroupMaxHcp, setOpenGroupMaxHcp] = useState('')
  const [openGroupNote, setOpenGroupNote] = useState('')
  const openGroupNoteRef = useRef(null)
  const liveQuoteRequestSeqRef = useRef(0)

  const applyCurrencyFromPayload = (payload) => {
    if (!payload) return

    const code =
      payload?.currency_code ||
      payload?.currencyCode ||
      payload?.data?.currency_code ||
      payload?.data?.currencyCode

    const symbol =
      payload?.currency_symbol ||
      payload?.currencySymbol ||
      payload?.data?.currency_symbol ||
      payload?.data?.currencySymbol

    if (code) setCurrencyCode(code)
    if (symbol) setCurrencySymbol(symbol)
  }

  const maxPlayers = useMemo(() => slot?.capacity || course?.max_players || 4, [course?.max_players, slot?.capacity])
  const allowGuests = Boolean(course?.non_members_allowed || course?.allow_non_member_play)
  const addonQuantityCap = useMemo(() => Math.min(Math.max(maxPlayers || 1, 1), 8), [maxPlayers])
  const bookingWindowLimits = useMemo(
    () => getBookingWindowLimits(course?.booking_window, timeZone),
    [course?.booking_window, timeZone]
  )
  const openGroupAllowedSizes = useMemo(() => {
    const raw = course?.open_groups_allowed_sizes || []
    return Array.isArray(raw)
      ? raw
          .map((value) => Number.parseInt(value, 10))
          .filter((num) => Number.isFinite(num) && num > 0 && num <= 3)
      : []
  }, [course?.open_groups_allowed_sizes])
  const openGroupCreationEnabled = Boolean(
    course?.open_groups_enabled && course?.open_groups_join_mode !== 'request' && openGroupAllowedSizes.length > 0,
  )

  const autoGrowTextarea = (el) => {
    if (!el) return
    // Reset height to auto first so scrollHeight is measured correctly
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }

  const [players, setPlayers] = useState(() => {
    const primaryName =
      [member?.first_name || member?.firstName, member?.last_name || member?.lastName]
        .filter(Boolean)
        .join(' ') ||
      member?.first_name ||
      member?.firstName ||
      'Primary member'
    const defaults = [
      buildPlayer({
        name: primaryName,
        nameIsFallback: ![member?.first_name, member?.firstName, member?.last_name, member?.lastName].some(Boolean),
        member_id: member?.id || null,
        type: 'member',
        locked: true,
      }),
    ]
    return defaults
  })

  const activePlayers = useMemo(() => players.slice(0, maxPlayers), [maxPlayers, players])
  const openGroupSizeOptions = useMemo(
    () => openGroupAllowedSizes.filter((size) => size + activePlayers.length <= 4),
    [activePlayers.length, openGroupAllowedSizes]
  )

  const isHolesSelected = useMemo(() => holes === 9 || holes === 18, [holes])

  const playerErrors = useMemo(() => {
    const errs = []
    activePlayers.forEach((p, idx) => {
      const trimmedName = p?.name?.trim()
      if (idx === 0) {
        if (!trimmedName) errs[idx] = "portalPresentation.member_member_name_required"
        return
      }

      if (!trimmedName) return

      const isMember = p?.type === 'member'
      const isGuest = p?.type === 'guest'

      if (!allowGuests && !p.member_id) {
        errs[idx] = "portalPresentation.member_guests_are_not_allowed_for_this_course_please_select_a_member"
      } else if (isMember && !p.member_id) {
        errs[idx] = "portalPresentation.member_select_a_member_from_the_list"
      } else if (isGuest) {
        if (!allowGuests) {
          errs[idx] = "portalPresentation.member_guests_are_not_allowed_for_this_course_please_select_a_member"
        } else if (!p.guestEmail || !p.guestPhone) {
          errs[idx] = "portalPresentation.member_guest_full_name_email_and_phone_are_required"
        }
      }
    })
    return errs
  }, [activePlayers, allowGuests])

  useEffect(() => {
    const preferredTz = resolveCourseTimeZone(course || {})
    if (preferredTz && preferredTz !== timeZone) {
      setTimeZone(preferredTz)
    }
  }, [course, timeZone])

  useEffect(() => {
    if (selectedDateKey) return
    const fromSlot = normalizeDateFromSlot(slot || initialSlotState, timeZone)
    const todayKey = getTodayDateKey(timeZone)
    const next = fromSlot || todayKey
    if (next) {
      setSelectedDateKey(next)
    }
  }, [initialSlotState, selectedDateKey, slot, timeZone])

  useEffect(() => {
    const todayKey = bookingWindowLimits.todayKey
    if (!todayKey) return
    const clamped = clampDateKey(selectedDateKey || todayKey, todayKey, bookingWindowLimits.maxDateKey)
    if (clamped && clamped !== selectedDateKey) {
      setSelectedDateKey(clamped)
    }
  }, [bookingWindowLimits.maxDateKey, bookingWindowLimits.todayKey, selectedDateKey])

  useEffect(() => {
    if (openGroupEnabled) {
      if (!openGroupSizeOptions.length) {
        setOpenGroupSpots(null)
      } else if (
        !Number.isFinite(Number(openGroupSpots)) ||
        !openGroupSizeOptions.includes(Number(openGroupSpots))
      ) {
        setOpenGroupSpots(openGroupSizeOptions[0])
      }
    }
  }, [openGroupEnabled, openGroupSizeOptions, openGroupSpots])

  useEffect(() => {
    if (openGroupEnabled && openGroupNoteRef.current) {
      autoGrowTextarea(openGroupNoteRef.current)
    }
  }, [openGroupEnabled])

  useEffect(() => {
    setSlot(null)
  }, [courseId, selectedDateKey, teeTimeId])

  useEffect(() => {
    let cancelled = false

    async function loadCourse() {
      if (!clubCode || !courseId) return
      setLoading(true)
      setError(null)

      try {
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/golf/courses`)
        const json = await res.json().catch(() => null)
        if (!res.ok) throw new Error(json?.error || 'unable_to_load_course')
        const found = Array.isArray(json?.data) ? json.data.find((c) => c.id === courseId) : null
        if (!cancelled) {
          applyCurrencyFromPayload(json)
          setCourse(found || null)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load course', err)
          setError("portalPresentation.member_unable_to_load_course_details_right_now")
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    if (!course) {
      loadCourse()
    } else {
      setLoading(false)
    }

    return () => {
      cancelled = true
    }
  }, [clubCode, course, courseId, memberPortalApiGet])

  useEffect(() => {
    let cancelled = false

    async function loadSlot() {
      if (!clubCode || !courseId || !selectedDateKey) return
      setError(null)
      setLoading(true)
      try {
        const res = await memberPortalApiGet(
          `/api/members-portal/${clubCode}/golf/courses/${courseId}/slots?date=${selectedDateKey}`
        )
        const json = await res.json().catch(() => null)
        if (!res.ok) throw new Error(json?.error || 'unable_to_load_tee_time')
        const found = Array.isArray(json?.data) ? json.data.find((s) => String(s.id) === String(teeTimeId)) : null
        if (!cancelled) {
          applyCurrencyFromPayload(json)
          setSlot(found || null)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load tee slot', err)
          setError("portalPresentation.member_unable_to_load_this_tee_time_please_return_to_tee_times_and_try_again")
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    if (!slot) {
      loadSlot()
    }

    return () => {
      cancelled = true
    }
  }, [clubCode, courseId, holes, memberPortalApiGet, selectedDateKey, slot, teeTimeId])

  useEffect(() => {
    let cancelled = false

    async function loadAddons() {
      if (!clubCode || !courseId) return
      try {
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/golf/courses/${courseId}/addons`)
        const json = await res.json().catch(() => null)
        if (!res.ok) throw new Error(json?.error || 'unable_to_load_addons')
        if (!cancelled) {
          applyCurrencyFromPayload(json)
          setAddons(Array.isArray(json?.data) ? json.data : [])
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Unable to load addons', err)
          setAddons([])
        }
      }
    }

    loadAddons()

    return () => {
      cancelled = true
    }
  }, [clubCode, courseId, memberPortalApiGet])

  useEffect(() => {
    if (!openGroupCreationEnabled && openGroupEnabled) {
      setOpenGroupEnabled(false)
    }
  }, [openGroupCreationEnabled, openGroupEnabled])

  useEffect(() => {
    let cancelled = false

    async function loadPricing() {
      if (!clubCode || !courseId) return

      try {
        const res = await memberPortalApiGet(
          `/api/members-portal/${clubCode}/golf/courses/${courseId}/pricing`
        )
        const json = await res.json().catch(() => null)

        if (!cancelled) {
          if (res.ok) {
            applyCurrencyFromPayload(json)
            setPricing(Array.isArray(json?.pricing) ? json.pricing : [])
          } else {
            console.warn('Failed to load course pricing', json?.error)
            setPricing([])
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Failed to load course pricing', err)
          setPricing([])
        }
      }
    }

    loadPricing()

    return () => {
      cancelled = true
    }
  }, [clubCode, courseId, memberPortalApiGet])

  useEffect(() => {
    return () => {
      Object.values(memberSearchTimersRef.current || {}).forEach((timer) => clearTimeout(timer))
      Object.values(memberSearchControllersRef.current || {}).forEach((controller) => {
        if (controller?.abort) controller.abort()
      })
    }
  }, [])

  const summary = useMemo(
    () => ({
      courseName: course?.name || 'Course',
      dateLabel: formatDateLabel(selectedDateKey || slot?.slot_time, timeZone, locale, memberText),
      timeLabel: formatTimeLabel(slot?.slot_time, timeZone, locale),
      maxPlayers,
    }),
    [course?.name, maxPlayers, selectedDateKey, slot?.slot_time, timeZone, locale, memberText]
  )

  const holeOptions = useMemo(() => {
    const baseOptions = new Set([9, 18])

    const holeCount = Number(course?.holes_count)
    if (holeCount === 9) {
      baseOptions.clear()
      baseOptions.add(9)
    }

    if (course?.nine_hole_booking_blocked === true) {
      baseOptions.delete(9)
    }

    const maxHoles = Number(slot?.max_holes || course?.max_holes)
    if (maxHoles === 9) {
      baseOptions.delete(18)
    }

    const allowedFromData = new Set()
    const addAllowed = (raw) => {
      if (!raw) return

      let list = []
      if (Array.isArray(raw)) {
        list = raw
      } else if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw)
          list = Array.isArray(parsed) ? parsed : [raw]
        } catch (err) {
          list = raw.split(',')
        }
      }

      list.forEach((value) => {
        const normalized = Number(value)
        if (normalized === 9 || normalized === 18) {
          allowedFromData.add(normalized)
        }
      })
    }

    addAllowed(slot?.allowed_holes)
    addAllowed(course?.allowed_holes)
    addAllowed(course?.holes_supported)

    let options = Array.from(baseOptions)

    if (allowedFromData.size > 0) {
      options = options.filter((opt) => allowedFromData.has(opt))
    }

    if (options.length === 0) {
      if (baseOptions.size === 0 && allowedFromData.size > 0) {
        options = Array.from(allowedFromData)
      } else {
        options = Array.from(baseOptions)
      }
    }

    const finalOptions = options.filter((opt) => opt === 9 || opt === 18)

    if (finalOptions.length === 0) {
      return [9, 18]
    }

    return finalOptions.sort((a, b) => a - b)
  }, [course?.allowed_holes, course?.holes_count, course?.holes_supported, course?.max_holes, course?.nine_hole_booking_blocked, slot?.allowed_holes, slot?.max_holes])

  useEffect(() => {
    if (holes != null) return

    const hasCourseData = Boolean(course)
    const hasSlotData = Boolean(slot)

    if (!hasCourseData && !hasSlotData) return
    if (!holeOptions.length) return

    if (holeOptions.includes(18)) {
      setHoles(18)
    } else if (holeOptions.includes(9)) {
      setHoles(9)
    } else {
      setHoles(holeOptions[0])
    }
  }, [course, holeOptions, holes, slot])

  const setAddonQuantity = (addonKey, quantity) => {
    const safeQuantity = Math.min(Math.max(Number.parseInt(quantity, 10) || 0, 0), addonQuantityCap)
    setSelectedAddonsByKey((prev) => {
      if (safeQuantity <= 0) {
        const { [addonKey]: _removed, ...rest } = prev
        return rest
      }
      return { ...prev, [addonKey]: safeQuantity }
    })
  }

  const incrementAddon = (addonKey) => {
    const current = Number.parseInt(selectedAddonsByKey?.[addonKey], 10) || 0
    setAddonQuantity(addonKey, current + 1)
  }

  const decrementAddon = (addonKey) => {
    const current = Number.parseInt(selectedAddonsByKey?.[addonKey], 10) || 0
    setAddonQuantity(addonKey, current - 1)
  }

  const findMemberMatch = (value, index) => {
    const trimmed = value?.trim()?.toLowerCase() || ''
    if (!trimmed) return null
    const results = memberResultsByIndex[index] || []
    return results.find((m) => {
      const fullName = (m.full_name || [m.first_name, m.last_name].filter(Boolean).join(' '))
        .trim()
        .toLowerCase()
      return fullName && fullName === trimmed
    })
  }

  const updatePlayer = (index, next) => {
    setPlayers((prev) => prev.map((p, idx) => (idx === index ? { ...p, ...next } : p)))
  }

  const handlePlayerNameChange = (index, value) => {
    const matchedMember = findMemberMatch(value, index)
    const trimmed = value.trim()
    const nextType = matchedMember ? 'member' : trimmed ? (allowGuests ? 'guest' : null) : null
    const shouldClearGuest = !matchedMember && !trimmed
    setPlayers((prev) => {
      const isLocked = prev[index]?.locked
      const nextPlayers = prev.map((p, idx) =>
        idx === index
          ? {
              ...p,
              name: value,
              type: isLocked ? 'member' : nextType,
              member_id: matchedMember ? matchedMember.id : null,
              guestEmail: matchedMember || shouldClearGuest ? '' : p.guestEmail,
              guestPhone: matchedMember || shouldClearGuest ? '' : p.guestPhone,
            }
          : p
      )
      scheduleMemberSearch(index, value, isLocked)
      return nextPlayers
    })
  }

  const handleOpenGroupNoteChange = (e) => {
    setOpenGroupNote(e.target.value)
    autoGrowTextarea(e.target)
  }

  const scheduleMemberSearch = (index, value, isLocked) => {
    const query = value.trim()

    if (memberSearchTimersRef.current[index]) {
      clearTimeout(memberSearchTimersRef.current[index])
    }

    if (memberSearchControllersRef.current[index]) {
      memberSearchControllersRef.current[index].abort()
      delete memberSearchControllersRef.current[index]
    }

    if (!clubCode || isLocked || query.length < 2) {
      setMemberResultsByIndex((prev) => ({ ...prev, [index]: [] }))
      return
    }

    const controller = new AbortController()
    memberSearchControllersRef.current[index] = controller

    memberSearchTimersRef.current[index] = setTimeout(async () => {
      try {
        const res = await memberPortalApiGet(
          `/api/members-portal/${clubCode}/members/search?q=${encodeURIComponent(query)}`,
          { signal: controller.signal }
        )
        const json = await res.json().catch(() => null)
        if (!res.ok) throw new Error(json?.error || 'unable_to_search_members')
        setMemberResultsByIndex((prev) => ({
          ...prev,
          [index]: Array.isArray(json?.data) ? json.data : [],
        }))
      } catch (err) {
        if (err?.name === 'AbortError') return
        console.warn('Member search failed', err)
        setMemberResultsByIndex((prev) => ({ ...prev, [index]: [] }))
      } finally {
        delete memberSearchControllersRef.current[index]
      }
    }, 275)
  }

  const handleSelectMember = (index, memberData) => {
    if (!memberData) return
    const fullName = (memberData.full_name || [memberData.first_name, memberData.last_name].filter(Boolean).join(' ')).trim()
    updatePlayer(index, {
      name: fullName,
      type: 'member',
      member_id: memberData.id,
      guestEmail: '',
      guestPhone: '',
    })
  }

  const addPlayer = () => {
    if (players.length >= maxPlayers) return
    setPlayers((prev) => [...prev, buildPlayer()])
  }

  const removePlayer = (index) => {
    if (index === 0) return
    setPlayers((prev) => prev.filter((_, idx) => idx !== index))
  }

  const validationErrors = useMemo(() => {
    const errs = []
    const filledPlayers = activePlayers.filter((p) => p.name?.trim())
    if (filledPlayers.length === 0) {
      errs.push("portalPresentation.member_at_least_one_player_is_required")
    }
    if (attemptedSubmit && !isHolesSelected) {
      errs.push("portalPresentation.member_please_select_holes_9_or_18")
    }
    if (openGroupCreationEnabled && openGroupEnabled) {
      const openSpotCount = Number.isFinite(Number(openGroupSpots)) ? Number(openGroupSpots) : null
      if (!openGroupSizeOptions.length || openSpotCount === null) {
        errs.push("portalPresentation.member_select_how_many_players_you_want_to_join_your_open_group")
      }
      const totalPlayersWithOpenSpots = filledPlayers.length + (openSpotCount || 0)
      if (totalPlayersWithOpenSpots > 4) {
        errs.push("portalPresentation.member_open_group_spots_would_exceed_the_maximum_of_4_players")
      }
    }
    playerErrors.forEach((err) => {
      if (err) errs.push(err)
    })
    return errs
  }, [activePlayers, attemptedSubmit, isHolesSelected, openGroupEnabled, openGroupSizeOptions.length, openGroupSpots, playerErrors])

  const selectedAddonDetails = useMemo(
    () =>
      addons
        .map((addon) => ({
          addon,
          quantity: Number.parseInt(selectedAddonsByKey?.[addon.key], 10) || 0,
        }))
        .filter(({ addon, quantity }) => addon?.id && quantity > 0)
        .map(({ addon, quantity }) => ({
          key: addon.key,
          id: addon.id,
          name: addon.name || addon.label,
          price: addon.price,
          quantity,
        })),
    [addons, selectedAddonsByKey]
  )

  const quotePlayersPayload = useMemo(
    () =>
      activePlayers
        .map((p, index) => {
          if (!p.name?.trim()) return null
          const fallbackMemberId = p.type === 'member' && !p.member_id && index === 0 ? member?.id || null : null
          return {
            type: p.type,
            member_id: p.member_id || fallbackMemberId,
            name: p.name,
          }
        })
        .filter(Boolean),
    [activePlayers, member?.id]
  )

  const bookingPlayersPayload = useMemo(
    () =>
      activePlayers
        .map((p, index) => {
          if (!p.name?.trim()) return null
          const fallbackMemberId = p.type === 'member' && !p.member_id && index === 0 ? member?.id || null : null
          return {
            name: p.name,
            type: p.type,
            member_id: p.member_id || fallbackMemberId,
            guest_email: p.guestEmail,
            guest_phone: p.guestPhone,
          }
        })
        .filter(Boolean),
    [activePlayers, member?.id]
  )

  useEffect(() => {
    const teeSlotId = typeof slot?.id === 'string' ? slot.id.trim() : ''
    const hasValidTeeSlotId = UUID_PATTERN.test(teeSlotId)
    const effectiveCourseId = typeof slot?.course_id === 'string' ? slot.course_id : courseId
    const effectiveSlotTime = slot?.slot_time || null

    if (!holes || !hasValidTeeSlotId || !quotePlayersPayload.length || !effectiveCourseId || !effectiveSlotTime) {
      liveQuoteRequestSeqRef.current += 1
      setLiveQuoteTotal(null)
      setLiveQuoteError(null)
      setLiveQuoteLoading(false)
      return undefined
    }

    const requestSeq = liveQuoteRequestSeqRef.current + 1
    liveQuoteRequestSeqRef.current = requestSeq
    const controller = new AbortController()
    setLiveQuoteLoading(true)
    setLiveQuoteTotal(null)
    setLiveQuoteError(null)
    const debounceTimer = window.setTimeout(async () => {
      try {
        const quotePayload = {
          course_id: effectiveCourseId,
          tee_slot_id: teeSlotId,
          slot_time: effectiveSlotTime,
          holes,
          players: quotePlayersPayload,
          addons: selectedAddonDetails,
        }
        if (import.meta.env.DEV) {
          console.info('[members-portal:golf-quote] outbound payload', quotePayload)
        }
        const res = await memberPortalApiPost(`/api/members-portal/${clubCode}/golf/tee-times/quote`, quotePayload, {
          signal: controller.signal,
        })
        const json = await res.json().catch(() => null)
        if (liveQuoteRequestSeqRef.current !== requestSeq) return

        const quote = json?.quote || json?.data?.quote
        const quoteOk = Boolean(res.ok && json?.ok !== false && quote?.ok)
        if (quoteOk) {
          const total = Number(quote.total)
          setLiveQuoteTotal(Number.isFinite(total) ? total : null)
          setLiveQuoteError(null)
          applyCurrencyFromPayload(json)
          return
        }

        const quoteError = json?.error || quote?.error || null
        if (quoteError === 'tee_slot_not_found') {
          setLiveQuoteError("portalPresentation.member_this_tee_time_is_no_longer_available_please_reselect_a_tee_time")
        } else {
          setLiveQuoteError(quoteError === 'pricing_not_found' || quoteError === 'missing_price_for_player_type'
            ? "portalPresentation.member_we_can_t_price_this_tee_time_right_now_please_try_another_time_or_contact_the_club"
            : "portalPresentation.member_we_couldn_t_calculate_your_booking_price_try_again_in_a_moment")
        }
      } catch (err) {
        if (err?.name === 'AbortError') return
        if (liveQuoteRequestSeqRef.current !== requestSeq) return
        console.warn('Failed to fetch live quote for member booking', err)
        setLiveQuoteError("portalPresentation.member_we_couldn_t_calculate_your_booking_price_try_again_in_a_moment")
      } finally {
        if (liveQuoteRequestSeqRef.current === requestSeq) setLiveQuoteLoading(false)
      }
    }, 320)

    return () => {
      window.clearTimeout(debounceTimer)
      controller.abort()
    }
  }, [clubCode, courseId, holes, memberPortalApiPost, quotePlayersPayload, quoteRetry, selectedAddonDetails, slot?.course_id, slot?.id, slot?.slot_time])


  const estimatedCost = liveQuoteTotal

  const formatPrice = (value) => {
    if (value == null) return '—'
    const amount = Number(value)
    const safeAmount = Number.isFinite(amount) ? amount : 0
    const symbol = currencySymbol || '£'
    return `${symbol}${safeAmount.toFixed(2)}`
  }


  const handleSubmit = async (event) => {
    event.preventDefault()
    setAttemptedSubmit(true)
    if (validationErrors.length > 0 || saving || liveQuoteLoading || liveQuoteTotal == null || liveQuoteError || !isHolesSelected) return
    setSaving(true)
    setFormMessage(null)
    setSubmitError(null)

    let checkoutWindow = null
    try {
      const payload = {
        course_id: courseId,
        tee_slot_id: slot?.id || teeTimeId,
        tee_time_id: teeTimeId,
        date: selectedDateKey,
        holes,
        players: bookingPlayersPayload,
        addons: selectedAddonDetails,
        payment_method: paymentMethod === 'pay_now' ? 'pay_now' : 'account',
        estimated_amount: Number(liveQuoteTotal ?? 0),
      }

      if (openGroupCreationEnabled && openGroupEnabled && Number.isFinite(Number(openGroupSpots))) {
        payload.is_open_group = true
        payload.open_group_spots_total = Number(openGroupSpots)
        payload.open_group_min_hcp = openGroupMinHcp || null
        payload.open_group_max_hcp = openGroupMaxHcp || null
        payload.open_group_note = openGroupNote || null
      }

      if (paymentMethod === 'pay_now') {
        const handoff = createPayNowHandoff({ module: 'golf', clubCode, successPath: `/${clubCode}/golf/booking/success` })
        checkoutWindow = openPayNowCheckoutPlaceholder(handoff, { locale })
        const checkoutRes = await memberPortalApiPost(`/api/members-portal/${clubCode}/booking-payments/checkout`, { platform: checkoutPlatform(), module: 'golf', booking_payload: payload })
        const checkoutJson = await checkoutRes.json().catch(() => null)
        if (checkoutRes.ok && checkoutJson?.status === 'completed' && checkoutJson.booking_id) { checkoutWindow.close(); navigate(`/${clubCode}/golf/booking/success?booking_id=${checkoutJson.booking_id}`); return }
        if (!checkoutRes.ok || !checkoutJson?.url) throw new Error(checkoutJson?.error || 'checkout_failed')
        updatePayNowHandoff(handoff.key, { checkout_session_id: checkoutJson.checkout_session_id || checkoutJson.session_id || checkoutJson.sessionId || null, payment_attempt_id: checkoutJson.payment_attempt_id || checkoutJson.paymentAttemptId || null })
        await checkoutWindow.location.assign(checkoutJson.url)
        return
      }

      const res = await memberPortalApiPost(
        `/api/members-portal/${clubCode}/golf/tee-times/bookings`,
        payload
      )
      const json = await res.json().catch(() => null)

      if (res.ok && json?.ok) {
        const canonicalBooking = createCommittedBooking({
          id: json.booking_id, module: 'golf', title: summary.courseName, location: summary.courseName,
          startAt: slot?.slot_time, timeZone, imageUrl: course?.course_image_url, imageFocusX: course?.course_image_focus_x,
          imageFocusY: course?.course_image_focus_y, amount: Number(liveQuoteTotal), currency: currencyCode,
          details: { holes, participants: bookingPlayersPayload, payment_method: 'member_account', payment_summary: 'Charged to member account' },
        })
        commitMemberBookingSuccess(canonicalBooking, { clubCode })
        navigate(`/${clubCode}/golf/booking/success?booking_id=${json.booking_id}`, { state: { booking: canonicalBooking } })
        return
      }

      if (!res.ok || !json?.ok) {
        const errorMsg = "portalPresentation.member_we_could_not_complete_the_booking_please_try_again"
        setSubmitError(errorMsg)
      }
    } catch (err) {
      checkoutWindow?.close?.()
      console.warn('[members-checkout]', { module: 'golf', boundary: 'submit', code: /^[a-z_]{1,64}$/.test(err?.message || '') ? err.message : 'checkout_failed' })
      setSubmitError(err?.message === 'payment_settlement_pending' ? 'memberAccount.settlementPending' : err?.message === 'checkout_open_failed' ? 'memberAccount.checkoutOpenFailed' : 'portalPresentation.member_we_could_not_complete_the_booking_please_check_your_details_and_try_again')
    } finally {
      setSaving(false)
    }
  }

  const courseImage = course?.course_image_url || course?.image_url || course?.hero_image_url || null
  const teeTimesPath = `/${clubCode}/golf/${courseId}${selectedDateKey ? `?date=${selectedDateKey}` : ''}`
  const teeTimesState = course || selectedDateKey ? { course, date: selectedDateKey } : undefined

  return (
    <div className="mp-tee-booking mp-tee-booking--golf">
      <div className="mp-tee-booking__header">
        <BackPillButton className="mp-tee-booking__back" to={teeTimesPath} state={teeTimesState}> {memberText("portalPresentation.member_back_to_tee_times")} </BackPillButton>
        <div className="mp-tee-booking__title">
          <h3>
            <strong>{summary.courseName}</strong> – {summary.dateLabel} – {summary.timeLabel}
          </h3>
        </div>
        <div aria-hidden className="mp-tee-booking__spacer" />
      </div>

      {error && <div className="mp-warning">{memberText(error)}</div>}

      <form className="mp-tee-booking__grid" onSubmit={handleSubmit}>
        <div className="mp-tee-booking__media">
          <div className="mp-tee-booking__image" aria-hidden style={courseImage ? { backgroundImage: `url(${courseImage})`, '--mp-course-image-position': course?.course_image_position || course?.image_focal_position || 'center' } : undefined} />
          <div className="mp-tee-booking__compact-summary">
            <strong>{summary.courseName}</strong>
            <span>{summary.dateLabel} · {summary.timeLabel} · {holes || '—'} {memberText("portalPresentation.member_holes")}</span>
          </div>
        </div>

        <div className="mp-tee-booking__forms">
          <div className="mp-card mp-tee-booking__desktop-details">
            <div className="mp-card__header">
              <div>
                <p className="mp-eyebrow">{memberText("facilityBooking.details")}</p>
                <h4>{memberText("portalPresentation.member_confirm_your_time")}</h4>
              </div>
            </div>
            <div className="mp-card__body grid two-col">
              <div>
                <p className="mp-label">{memberText("golf.print.course")}</p>
                <p className="mp-value">{summary.courseName}</p>
              </div>
              <div>
                <p className="mp-label">{memberText("portalPresentation.member_date")}</p>
                <p className="mp-value">{summary.dateLabel}</p>
              </div>
              <div>
                <p className="mp-label">{memberText("facilityBooking.time")}</p>
                <p className="mp-value">{summary.timeLabel}</p>
              </div>
              <div>
                <p className="mp-label">{memberText("portalPresentation.member_holes_caption")}</p>
                <div className="mp-toggle-group" role="group" aria-label={memberText("golf.course.selectHoles")}>
                  {holeOptions.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      className={holes === opt ? 'active' : ''}
                      onClick={() => setHoles(opt)}
                    >
                      {opt}{memberText("portalPresentation.member__hole")} </button>
                  ))}
                </div>
                {attemptedSubmit && !isHolesSelected && (
                  <p className="mp-inline-error">{memberText("portalPresentation.member_please_select_holes_to_continue")}</p>
                )}
              </div>
            </div>
          </div>

          <div className="mp-card">
            <div className="mp-card__header mp-card__header--compact">
              <div className="mp-flex mp-flex--between">
                <h4>{memberText("portalPresentation.member_players_caption")}</h4>
                {openGroupCreationEnabled && openGroupEnabled && (
                  <span className="mp-label mp-label--pill">{memberText("portalPresentation.member_open_group")}</span>
                )}
              </div>
            </div>
            <div className="mp-card__body">
              {openGroupCreationEnabled ? <fieldset className="mp-open-play-configuration">
                <legend>{memberText('portalPresentation.member_booking_type')}</legend>
                <div className="mp-toggle-group" role="radiogroup" aria-label={memberText('portalPresentation.member_booking_type')}>
                  <button type="button" role="radio" aria-checked={!openGroupEnabled} className={!openGroupEnabled ? 'active' : ''} onClick={() => setOpenGroupEnabled(false)}>{memberText('portalPresentation.member_private_booking')}</button>
                  <button type="button" role="radio" aria-checked={openGroupEnabled} className={openGroupEnabled ? 'active' : ''} disabled={!openGroupSizeOptions.length} onClick={() => setOpenGroupEnabled(true)}>{memberText('portalPresentation.member_open_group')}</button>
                </div>
                <p className="mp-text-muted">{memberText('portalPresentation.member_open_group_help')}</p>
              </fieldset> : null}
              {activePlayers.map((player, index) => {
                const statusLabel =
                  player.type === 'member'
                    ? memberText("portalPresentation.member_member")
                    : player.type === 'guest'
                      ? memberText("portalPresentation.member_guest")
                      : memberText("portalPresentation.member_add_name")
                const memberSuggestions = memberResultsByIndex[index] || []
                const showGuestFields = allowGuests && player.type === 'guest'

                return (
                  <div key={`player-${index}`} className={`mp-player-row${index === 0 && player.locked ? ' mp-player-row--primary' : ''}`}>
                    <div className="mp-player-row__header">
                      <span className="mp-label">{memberText("portalPresentation.member_player_caption")} {index + 1}</span>
                      <div className="mp-player-row__header-actions">
                        <div className={`mp-player-status ${player.type || 'neutral'}`}>
                          {statusLabel}
                        </div>
                        {index > 0 && (
                          <button type="button" className="mp-link" onClick={() => removePlayer(index)}> {memberText("portalPresentation.member_remove")} </button>
                        )}
                      </div>
                    </div>
                    {index === 0 && player.locked && (
                      <div className="mp-player-row__primary-name">{player.nameIsFallback ? memberText("portalPresentation.member_primary_member") : player.name}</div>
                    )}
                    <div className="mp-player-row__inputs">
                      <label className="mp-input">
                        <span className="mp-label">{player.type === 'guest' ? memberText("portalPresentation.member_guest_full_name") : memberText("portalPresentation.member_name")}</span>
                        <input
                          type="text"
                          value={player.name}
                          onChange={(e) => handlePlayerNameChange(index, e.target.value)}
                          onBlur={() => {
                            const match = findMemberMatch(player.name, index)
                            if (match) handleSelectMember(index, match)
                          }}
                          placeholder={
                            player.locked
                              ? memberText("portalPresentation.member_member_on_file")
                              : allowGuests
                                ? memberText("portalPresentation.member_search_member_or_enter_guest_name")
                                : memberText("portalPresentation.member_search_and_select_a_member")
                          }
                          required={index === 0}
                          disabled={player.locked}
                        />
                        {memberSuggestions.length > 0 && (
                          <>
                            <div className="mp-tee-booking__suggestions" role="listbox">
                              {memberSuggestions.map((m) => {
                                const fullName = (m.full_name || [m.first_name, m.last_name].filter(Boolean).join(' ')).trim()
                                return (
                                  <button
                                    type="button"
                                    key={m.id}
                                    className="mp-tee-booking__suggestion"
                                    onClick={() => handleSelectMember(index, m)}
                                  >
                                    {fullName}
                                  </button>
                                )
                              })}
                            </div>
                          </>
                        )}
                      </label>
                    </div>
                    {showGuestFields && (
                      <div className="mp-guest-inline">
                        <label className="mp-input">
                          <span className="mp-label">{memberText("portalPresentation.member_guest_phone")}</span>
                          <input
                            type="tel"
                            value={player.guestPhone}
                            onChange={(e) => updatePlayer(index, { guestPhone: e.target.value })}
                            placeholder={memberText("portalPresentation.member_mobile_for_updates")}
                            required
                          />
                        </label>
                        <label className="mp-input">
                          <span className="mp-label">{memberText("golf.bookingModal.guestEmail")}</span>
                          <input
                            type="email"
                            value={player.guestEmail}
                            onChange={(e) => updatePlayer(index, { guestEmail: e.target.value })}
                            placeholder={memberText("portalPresentation.member_email_for_confirmation")}
                            required
                          />
                        </label>
                      </div>
                    )}
                {playerErrors[index] && <p className="mp-inline-error">{memberText(playerErrors[index])}</p>}
              </div>
            )
          })}
              <div className="mp-player-actions">
                <div className="mp-flex mp-flex--between mp-flex--align-center">
                  <div>
                    {activePlayers.length < maxPlayers && (
                      <button type="button" className="mp-btn mp-btn--ghost" onClick={addPlayer}> {memberText("portalPresentation.member__add_player")} </button>
                    )}
                  </div>

                </div>
              </div>
              {openGroupCreationEnabled && openGroupEnabled && (
                <div className="mp-open-group-fields">
                  <div className="mp-inline-fields">
                    <div>
                      <p className="mp-label">{memberText("portalPresentation.member_looking_for")}</p>
                      <div className="mp-toggle-group" role="group" aria-label={memberText("portalPresentation.member_open_group_size")}>
                        {openGroupSizeOptions.map((size) => (
                          <button
                            type="button"
                            key={`og-${size}`}
                            className={Number(openGroupSpots) === Number(size) ? 'active' : ''}
                            onClick={() => setOpenGroupSpots(size)}
                          >
                            {size} {memberText("portalPresentation.member_player")}{size > 1 ? 's' : ''}
                          </button>
                        ))}
                      </div>
                    </div>
                    {course?.open_groups_show_handicap_range && (
                      <div className="mp-inline-fields">
                        <label className="mp-input">
                          <span className="mp-label">{memberText("portalPresentation.member_min_handicap_optional")}</span>
                          <input
                            type="number"
                            step="0.1"
                            value={openGroupMinHcp}
                            onChange={(e) => setOpenGroupMinHcp(e.target.value)}
                            placeholder={memberText("portalPresentation.member_e_g_10_0")}
                          />
                        </label>
                        <label className="mp-input">
                          <span className="mp-label">{memberText("portalPresentation.member_max_handicap_optional")}</span>
                          <input
                            type="number"
                            step="0.1"
                            value={openGroupMaxHcp}
                            onChange={(e) => setOpenGroupMaxHcp(e.target.value)}
                            placeholder={memberText("portalPresentation.member_e_g_24_0")}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                  <label className="mp-input">
                    <span className="mp-label">{memberText("portalPresentation.member_note_for_guests_optional")}</span>
                    <textarea
                      rows="2"
                      value={openGroupNote}
                      ref={openGroupNoteRef}
                      onChange={handleOpenGroupNoteChange}
                      onInput={(e) => autoGrowTextarea(e.target)}
                      style={{ resize: 'none', overflow: 'hidden' }}
                      placeholder={memberText("portalPresentation.member_share_expectations_or_preferences")}
                    />
                  </label>
                  <p className="mp-text-muted">{memberText("portalPresentation.member_spots_available")} {Number(openGroupSpots) || 0}</p>
                </div>
              )}
            </div>
          </div>

          <div className="mp-card">
              <div className="mp-card__header">
                <div>
                  <p className="mp-eyebrow">{memberText("portalPresentation.member_add_ons")}</p>
                  <h4>{memberText("portalPresentation.member_enhance_your_round")}</h4>
                </div>
              </div>
              <div className="mp-card__body mp-addon-list">
                {addons.length === 0 ? (
                  <p className="mp-text-muted">{memberText("golf.bookingModal.noAddons")}</p>
                ) : (
                  addons.map((addon) => (
                    <div
                      key={addon.key}
                      className={`mp-addon${(selectedAddonsByKey?.[addon.key] || 0) > 0 ? ' selected' : ''}`}
                    >
                      <div className="mp-addon__meta">
                        <span>{addon.label || addon.name}</span>
                        {addon.price != null && (
                          <span className="mp-text-muted">{formatPrice(Number(addon.price) || 0)}</span>
                        )}
                      </div>
                      <div className="mp-addon__actions">
                        <button
                          type="button"
                          className="mp-btn mp-btn--ghost"
                          onClick={() => decrementAddon(addon.key)}
                          disabled={(Number.parseInt(selectedAddonsByKey?.[addon.key], 10) || 0) <= 0}
                        >
                          -
                        </button>
                        <span className="mp-addon__qty">{Number.parseInt(selectedAddonsByKey?.[addon.key], 10) || 0}</span>
                        <button
                          type="button"
                          className="mp-btn mp-btn--ghost"
                          onClick={() => incrementAddon(addon.key)}
                          disabled={(Number.parseInt(selectedAddonsByKey?.[addon.key], 10) || 0) >= addonQuantityCap}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

          </div>

        <aside className="mp-tee-booking__summary">
          <div className="mp-summary-card">
            <div className="mp-summary-header">
              <p className="mp-eyebrow">{memberText("facilityBooking.review")}</p>
              <h4>{memberText("portalPresentation.member_golf_booking")}</h4>
            </div>
            <div className="mp-summary-section">
              <div className="mp-summary-row">
                <Users size={16} />
                <div>
                  <p className="mp-label">{memberText("portalPresentation.member_players_caption")}</p>
                  <p className="mp-value">{activePlayers.filter((p) => p.name?.trim()).length} {memberText("portalPresentation.member_confirmed")}</p>
                </div>
              </div>
              {openGroupCreationEnabled && openGroupEnabled && (
                <div className="mp-summary-row">
                  <Shield size={16} />
                  <div>
                    <p className="mp-label">{memberText("portalPresentation.member_open_group_2_3")}</p>
                    <p className="mp-value">{memberText("portalPresentation.member_spots_available")} {Number(openGroupSpots) || 0}</p>
                  </div>
                </div>
              )}
              <div className="mp-summary-row">
                <Clock size={16} />
                <div>
                  <p className="mp-label">{memberText("facilityBooking.time")}</p>
                  <p className="mp-value">{summary.timeLabel}</p>
                </div>
              </div>
              <div className="mp-summary-row">
                <Shield size={16} />
                <div>
                  <p className="mp-label">{memberText("portalPresentation.member_add_ons")}</p>
                  <p className="mp-value">
                    {addons.some((addon) => Number.parseInt(selectedAddonsByKey?.[addon.key], 10) > 0)
                      ? addons
                          .map((addon) => {
                            const qty = Number.parseInt(selectedAddonsByKey?.[addon.key], 10) || 0
                            if (qty <= 0) return null
                            return `${addon.label || addon.name} x${qty}`
                          })
                          .filter(Boolean)
                          .join(', ')
                      : memberText("portalPresentation.member_none_selected")}
                  </p>
                </div>
              </div>
            </div>
            <MemberPaymentMethodOptions value={paymentMethod} onChange={setPaymentMethod} disabled={saving} />
            <div className="mp-summary-section total">
              <div>
                <p className="mp-label">{memberText("portalPresentation.member_estimated_total")}</p>
                <p className="mp-summary-total">{formatPrice(estimatedCost)}</p>
                {liveQuoteError && <div className="mp-inline-error"><p>{memberText(liveQuoteError)}</p><button type="button" className="mp-link" onClick={() => setQuoteRetry((value) => value + 1)}>{memberText("portalPresentation.member_retry_price")}</button></div>}
              </div>
              <div className="mp-summary-row subtle">
                <CreditCard size={16} />
                <span>{memberText("portalPresentation.member_includes_add_ons_selected")}</span>
              </div>
            </div>

            {validationErrors.length > 0 && (
              <div className="mp-inline-error">
                {validationErrors.map((msg) => (
                  <p key={msg}>{memberText(msg)}</p>
                ))}
              </div>
            )}

            {formMessage && <div className="mp-inline-success">{formMessage}</div>}
            {submitError && <div className="mp-inline-error">{memberText(submitError)}</div>}

            <button
              type="submit"
              className="mp-btn mp-btn--primary mp-btn--block mp-tee-booking__confirm-cta"
              disabled={validationErrors.length > 0 || saving || loading || liveQuoteLoading || liveQuoteTotal == null || Boolean(liveQuoteError) || !slot || !isHolesSelected}
            >
              {saving ? memberText("portalPresentation.confirming") : memberText("facilityBooking.confirm")}
            </button>
          </div>
        </aside>
        <div className={`mp-golf-checkout-bar${liveQuoteError ? ' mp-golf-checkout-bar--error' : ''}`} role="region" aria-label={memberText("portalPresentation.member_booking_checkout")}>
          {liveQuoteError && (
            <div className="mp-golf-checkout-error" role="alert">
              <span>{memberText(liveQuoteError)}</span>
              <button type="button" className="mp-link" onClick={() => setQuoteRetry((value) => value + 1)}>{memberText("portalPresentation.member_retry")}</button>
            </div>
          )}
          <div className="mp-golf-checkout-total"><span>{memberText("portalPresentation.member_total")}</span><strong>{liveQuoteLoading ? memberText("portalPresentation.member_calculating") : liveQuoteError ? memberText("portalPresentation.member_unavailable") : formatPrice(liveQuoteTotal)}</strong></div>
          <button type="submit" className="mp-btn mp-btn--primary" disabled={validationErrors.length > 0 || saving || loading || liveQuoteLoading || liveQuoteTotal == null || Boolean(liveQuoteError) || !slot || !isHolesSelected}>
            {saving ? memberText("portalPresentation.member_booking") : memberText("portalPresentation.member_book_tee_time")}
          </button>
        </div>
      </form>
    </div>
  )
}
